import { useMemo, useState } from "react";
import { Button, ButtonGroup, Col, Container, Dropdown, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import Flight, { FlightPassengerStopDetail, FlightPassengerStops } from "../../../models/flight";
import { Passenger } from "../../../models/passenger";
import { RoutePoint } from "../../../models/route";
import moment from "moment";

/**
 * "Passengers per leg" editor, opened from the flight form's Intermediate Stops section. The default
 * ("Same for all passengers") keeps the original single span; "Per passenger" sets each passenger's
 * boarding / leaving stop (W&B per leg, boarding passes) and per stop the standby time used in the
 * Field 18 RMK and, optionally, the boarding time printed on the passes (estimated when left empty).
 */

export interface PassengersPerLegModalProps {
  points: RoutePoint[]; //[departure, ...intermediateStops, destination]
  span: FlightPassengerStops; //the "Leg(s) with Passengers" span: every passenger's default
  passengers: Passenger[];
  passengerLegs: Flight["passengerLegs"];
  stopDetails: (FlightPassengerStopDetail | null)[] | undefined;
  departureDateTime: Date;
  //estimated arrival at each stop for the given standby times (distance / cruise speed, rounded up to 5')
  estimateArrivals: (standbyMinsByStop: (number | undefined)[]) => (Date | undefined)[];
  disabled: boolean;
  onApply: (passengerLegs: Flight["passengerLegs"], stopDetails: (FlightPassengerStopDetail | null)[] | undefined) => void;
  onCancel: () => void;
}

function PassengersPerLegModal({ points, span, passengers, passengerLegs, stopDetails, departureDateTime, estimateArrivals, disabled, onApply, onCancel }: PassengersPerLegModalProps) {
  const initiallyCustom = Object.keys(passengerLegs ?? {}).length > 0 || (stopDetails ?? []).some((d) => d);
  const [perPassenger, setPerPassenger] = useState(initiallyCustom);
  const [legs, setLegs] = useState<{ [id: string]: FlightPassengerStops }>({ ...(passengerLegs ?? {}) });
  const [details, setDetails] = useState<(FlightPassengerStopDetail | null)[]>([...(stopDetails ?? [])]);

  const stopsOf = (p: Passenger): FlightPassengerStops => (p.id && legs[p.id]) || span;
  const setStops = (p: Passenger, stops: FlightPassengerStops) => {
    if (!p.id) return;
    setLegs((prev) => {
      const next = { ...prev };
      if (stops.fromIndex === span.fromIndex && stops.toIndex === span.toIndex) delete next[p.id!];
      else next[p.id!] = stops;
      return next;
    });
  };

  //every intermediate stop of the flight — a passenger may board or leave at any of them
  const stopIndices = useMemo(() => points.map((_, i) => i).filter((i) => i > 0 && i < points.length - 1), [points]);
  const countAt = (i: number) => ({
    embarking: passengers.filter((p) => stopsOf(p).fromIndex === i).length,
    disembarking: passengers.filter((p) => stopsOf(p).toIndex === i).length,
  });
  const setDetail = (i: number, patch: Partial<FlightPassengerStopDetail>) =>
    setDetails((prev) => {
      const next = [...prev];
      while (next.length <= i) next.push(null);
      const merged = { ...(next[i] ?? {}), ...patch };
      (Object.keys(merged) as (keyof FlightPassengerStopDetail)[]).forEach((k) => merged[k] === undefined && delete merged[k]);
      next[i] = Object.keys(merged).length ? merged : null;
      return next;
    });

  //boarding time printed on the passes: the user's time when set, otherwise the estimate (not stored,
  //so it follows later changes to the departure time; the backend computes the same estimate)
  const estimates = useMemo(() => estimateArrivals(points.map((_, i) => details[i]?.standbyMins)), [estimateArrivals, points, details]);
  const hhmm = (d?: Date | string) => (d ? moment.utc(d).format("HH:mm") : "");
  const setTime = (i: number, value: string) => {
    if (!value) return setDetail(i, { boardingTime: undefined });
    const dep = moment.utc(departureDateTime);
    const [h, m] = value.split(":").map(Number);
    const t = dep.clone().set({ hour: h, minute: m, second: 0, millisecond: 0 });
    if (t.isBefore(dep)) t.add(1, "day");
    setDetail(i, { boardingTime: t.toISOString() });
  };

  const apply = () => {
    if (!perPassenger) return onApply(undefined, undefined);
    const cleanDetails = details.some((d) => d) ? details.map((d) => d ?? null) : undefined;
    onApply(Object.keys(legs).length ? legs : undefined, cleanDetails);
  };

  return (
    <Container fluid className="px-4">
      <Row className="py-5">
        <Col xs={12}>
          <h2 className="mb-1">Passengers per leg</h2>
          <div className="text-muted">
            {points.map((p) => p.name).join(" – ")} · used for the Weight &amp; Balance of each leg, the boarding passes and the Field 18 remark.
          </div>
        </Col>
      </Row>

      <ButtonGroup className="mb-4">
        <Button variant={perPassenger ? "outline-dark" : "dark"} disabled={disabled} onClick={() => setPerPassenger(false)}>
          Same for all passengers
        </Button>
        <Button variant={perPassenger ? "dark" : "outline-dark"} disabled={disabled} onClick={() => setPerPassenger(true)}>
          Per passenger
        </Button>
      </ButtonGroup>

      {!perPassenger ? (
        <p className="text-muted">
          Every passenger flies {points[span.fromIndex]?.name} – {points[span.toIndex]?.name}. Boarding time, standby time and the passenger change at the
          stops are set in the flight form, as before.
        </p>
      ) : (
        <>
          <h5 className="mt-2 mb-3">Passengers</h5>
          {passengers.map((p, idx) => {
            const stops = stopsOf(p);
            return (
              <Row key={p.id ?? idx} className="g-3 mb-3 align-items-center">
                <Col xs={12} md={4} className="fw-semibold">
                  {p.lastName} {p.firstName}
                </Col>
                <Col xs={6} md={4}>
                  <StopDropdown
                    label="Boards at"
                    disabled={disabled || !p.id}
                    value={points[stops.fromIndex]?.name ?? ""}
                    options={points.map((pt, i) => ({ index: i, name: pt.name })).filter((o) => o.index < points.length - 1)}
                    //boarding at or after the current leaving stop moves the leaving stop to the next one
                    onSelect={(i) => setStops(p, { fromIndex: i, toIndex: Math.max(stops.toIndex, i + 1) })}
                  />
                </Col>
                <Col xs={6} md={4}>
                  <StopDropdown
                    label="Leaves at"
                    disabled={disabled || !p.id}
                    value={points[stops.toIndex]?.name ?? ""}
                    options={points.map((pt, i) => ({ index: i, name: pt.name })).filter((o) => o.index > 0)}
                    //leaving at or before the boarding stop moves the boarding stop to the one before
                    onSelect={(i) => setStops(p, { fromIndex: Math.min(stops.fromIndex, i - 1), toIndex: i })}
                  />
                </Col>
              </Row>
            );
          })}

          {stopIndices.length > 0 && (
            <>
              <h5 className="mt-4 mb-3">Stops</h5>
              {stopIndices.map((i) => {
                const c = countAt(i);
                return (
                  <Row key={i} className="g-3 mb-3 align-items-center">
                    <Col xs={12} md={4}>
                      <div className="fw-semibold">{points[i].name}</div>
                      <div className="small text-muted">
                        {c.disembarking > 0 ? `${c.disembarking} leaving` : "nobody leaving"} · {c.embarking > 0 ? `${c.embarking} boarding` : "nobody boarding"}
                      </div>
                    </Col>
                    <Col xs={6} md={4}>
                      <div className="input-container rounded-lg">
                        <input
                          id={`stop-${i}-boarding`}
                          type="time"
                          className="bg-transparent"
                          disabled={disabled || c.embarking === 0}
                          value={c.embarking === 0 ? "" : hhmm(details[i]?.boardingTime ?? estimates[i])}
                          onChange={(e) => setTime(i, e.target.value)}
                        />
                        <label htmlFor={`stop-${i}-boarding`} className="filled">
                          Boarding time (UTC) · boarding pass only
                        </label>
                      </div>
                      <div className="small text-muted mt-1">
                        {c.embarking === 0 ? (
                          "Optional · only used when someone boards here"
                        ) : details[i]?.boardingTime ? (
                          <>
                            Set by you ·{" "}
                            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" disabled={disabled} onClick={() => setDetail(i, { boardingTime: undefined })}>
                              use the estimate {estimates[i] ? hhmm(estimates[i]) : ""}
                            </button>
                          </>
                        ) : estimates[i] ? (
                          "Optional · estimated from distance and cruise speed"
                        ) : (
                          "Optional · set a cruise speed to get an estimate"
                        )}
                      </div>
                    </Col>
                    <Col xs={6} md={4}>
                      <div className="input-container rounded-lg">
                        <input
                          id={`stop-${i}-standby`}
                          type="number"
                          min={0}
                          className="bg-transparent"
                          disabled={disabled}
                          value={details[i]?.standbyMins ?? ""}
                          onChange={(e) => {
                            const v = parseInt(e.target.value);
                            setDetail(i, { standbyMins: isNaN(v) ? undefined : Math.max(0, v) });
                          }}
                        />
                        <label htmlFor={`stop-${i}-standby`} className={details[i]?.standbyMins !== undefined ? "filled" : ""}>
                          Standby time (mins)
                        </label>
                      </div>
                    </Col>
                  </Row>
                );
              })}
            </>
          )}
        </>
      )}

      <Row className="py-4">
        <Col className="d-flex gap-2 justify-content-end">
          <Button variant="light" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="dark" disabled={disabled} onClick={apply} style={{ minWidth: 120 }}>
            Apply
          </Button>
        </Col>
      </Row>
    </Container>
  );
}

export default PassengersPerLegModal;

//The app's dropdown field: grey input box, floating label, caret — same markup as EntityBooleanToggle.
function StopDropdown({ label, value, options, onSelect, disabled }: { label: string; value: string; options: { index: number; name: string }[]; onSelect: (index: number) => void; disabled: boolean }) {
  return (
    <div className="input-container rounded-lg">
      <Dropdown onSelect={(k) => k !== null && onSelect(Number(k))}>
        <Dropdown.Toggle as={"input"} readOnly disabled={disabled} className="bg-transparent w-100" value={value} style={{ cursor: disabled ? "auto" : "pointer" }} />
        <Dropdown.Menu>
          {options.map((o) => (
            <Dropdown.Item key={o.index} eventKey={String(o.index)} active={o.name === value}>
              {o.name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
      <label className={value && "filled"}>{label}</label>
      <FontAwesomeIcon className="position-absolute" style={{ right: "16px", top: "50%", transform: "translateY(-50%)" }} icon={faCaretDown} />
    </div>
  );
}
